import { useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Alert, Button, Form, Spin } from 'antd'
import {
  DEFAULT_CLIENT_TEXT_COLOR,
  normalizeWorkspaceValue,
  type ClientFormValues,
} from '../clientFormShared'
import {
  useClientDetail,
  useDeleteClientMutation,
  usePbiGroups,
  useUpdateClientMutation,
  useUploadClientLogoMutation,
} from '../hooks'
import { normalizePhone } from '@/lib/phone'
import { useAuthSession } from '@/lib/session'
import type { ClientStatusId } from '../types'
import { ClientDetailsFormFields } from './components/ClientDetailsFormFields'
import { ClientLogoSection } from './components/ClientLogoSection'
import { DeleteClientModal } from './components/DeleteClientModal'
import { EditClientFooter } from './components/EditClientFooter'
import { EditClientHeader } from './components/EditClientHeader'
import { StatusConfirmModal } from './components/StatusConfirmModal'
import { useClientLogoSection, useEditClientCancel, useEditClientSave } from './hooks'

export default function EditClientPage() {
  const { clientId = '' } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const authSession = useAuthSession()
  const isAdmin = authSession?.currentUserTypeId === 1

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false)

  const { data: pbiGroups = [], isLoading: pbiGroupsLoading } = usePbiGroups()
  const {
    data: editDetail,
    isPending: editDetailPending,
    isError: detailError,
    error: detailQueryError,
  } = useClientDetail(clientId, Boolean(clientId))

  const updateMutation = useUpdateClientMutation()
  const deleteMutation = useDeleteClientMutation()
  const uploadClientLogoMutation = useUploadClientLogoMutation()

  const [form] = Form.useForm<ClientFormValues>()
  const watchedStatusId = Form.useWatch('statusId', form)
  const currentEditStatusId = watchedStatusId ?? editDetail?.statusId ?? 1

  const logo = useClientLogoSection(clientId)

  const { saveClient, savePending } = useEditClientSave({
    clientId,
    editDetail,
    form,
    pbiGroups,
    logoBase64: logo.logoBase64,
    logoContentType: logo.logoContentType,
    authSession,
    updateMutation,
    uploadClientLogoMutation,
  })

  const onCancel = useEditClientCancel({ form, resetLogoState: logo.resetLogoState })

  useEffect(() => {
    if (!editDetail) return

    form.setFieldsValue({
      name: editDetail.name,
      phone: normalizePhone(editDetail.phone),
      email: editDetail.email,
      principalContact: editDetail.principalContact,
      address: editDetail.address,
      street: editDetail.street || '',
      suburb: editDetail.suburb || '',
      state: editDetail.state || '',
      postcode: editDetail.postcode || '',
      statusId: editDetail.statusId,
      typeId: editDetail.typeId,
      showProjectStatus: editDetail.showProjectStatus ?? false,
      showAdvisoryMapping: editDetail.showAdvisoryMapping ?? false,
      powerBIWorkspace: normalizeWorkspaceValue(editDetail.powerBIWorkspace),
      powerBIWorkspaceName: editDetail.powerBIWorkspaceName || '',
      primaryColor: editDetail.primaryColor?.trim() || undefined,
      secondaryColor: editDetail.secondaryColor?.trim() || undefined,
      textColor: editDetail.textColor?.trim() || DEFAULT_CLIENT_TEXT_COLOR,
    })
  }, [editDetail, form])

  if (!clientId) {
    return <Navigate to="/clients" replace />
  }

  const detailFailed = detailError || (!editDetailPending && !editDetail)
  const coreSettingsLoading = !detailFailed && (editDetailPending || logo.clientLogoPending)

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-brand-100 shadow-card bg-gradient-to-br from-white via-white to-brand-50/40"
      data-testid="edit-client-page"
    >
      <div
        className="pointer-events-none absolute -top-20 -right-20 h-52 w-52 rounded-full bg-brand-100/60 blur-2xl"
        aria-hidden
      />
      <EditClientHeader />

      <div className="p-6 bg-white/70 backdrop-blur-[1px]">
        {detailFailed && (
          <Alert
            type="error"
            showIcon
            className="mb-4"
            message={(detailQueryError as Error)?.message || 'Failed to load client'}
            action={
              <Button size="small" onClick={() => navigate('/clients')}>
                Back to clients
              </Button>
            }
          />
        )}
        {coreSettingsLoading && (
          <div className="flex min-h-[320px] items-center justify-center py-12">
            <Spin size="large" />
          </div>
        )}

        {!detailFailed && !coreSettingsLoading && (
          <>
            <Form form={form} layout="vertical">
              {logo.logoError && <Alert type="error" showIcon className="mb-3" message={logo.logoError} />}
              <ClientDetailsFormFields
                form={form}
                pbiGroups={pbiGroups}
                pbiGroupsLoading={pbiGroupsLoading}
                isAdmin={isAdmin}
                logoSection={
                  <ClientLogoSection
                    logoUploadProps={logo.logoUploadProps}
                    logoFileName={logo.logoFileName}
                    displayedLogoUrl={logo.displayedLogoUrl}
                    deleteLogoPending={logo.deleteLogoPending}
                    onClearPendingLogo={logo.clearPendingLogo}
                    onDeleteLogo={logo.deleteLogo}
                  />
                }
              />
            </Form>

            <EditClientFooter
              currentEditStatusId={currentEditStatusId}
              statusActionLoading={updateMutation.isPending}
              deleteLoading={deleteMutation.isPending}
              saveLoading={savePending}
              onStatusClick={() => setStatusConfirmOpen(true)}
              onDeleteClick={() => setDeleteModalOpen(true)}
              onCancel={onCancel}
              onSave={saveClient}
            />
          </>
        )}
      </div>

      <StatusConfirmModal
        open={statusConfirmOpen}
        onClose={() => setStatusConfirmOpen(false)}
        form={form}
        editDetail={editDetail}
        pbiGroups={pbiGroups}
        logoBase64={logo.logoBase64}
        clientId={clientId}
        currentEditStatusId={currentEditStatusId as ClientStatusId}
        updateMutation={updateMutation}
      />

      <DeleteClientModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        clientId={clientId}
        clientName={editDetail?.name}
        deleteMutation={deleteMutation}
      />
    </div>
  )
}
